import * as S from "@/components/styled";
import { DEFAULT_DEBOUNCE_TIME } from "@/config";
import { useDebounce } from "@/hooks";
import { IMilestoneTemplate } from "@/interfaces";
import { useSearchMilestoneTemplatesByPropertiesQuery } from "@/services";
import { Input, Typography } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { doesDuplicateMilestoneValueExists } from "../helpers";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  projectMilestoneTemplates: IMilestoneTemplate[];
  propertyName: keyof IMilestoneTemplate;
  setProjectMilestoneTemplate(milestoneTemplate: IMilestoneTemplate): void;
  setIsValid: Dispatch<SetStateAction<object>>;
  selectedValue?: string;
  isDisabled?: boolean;
}

interface IQueryParams {
  property: string;
  value: string;
}

export const TemplateUniqueTextField = ({
  milestoneTemplate,
  projectMilestoneTemplates,
  propertyName,
  setProjectMilestoneTemplate,
  setIsValid,
  selectedValue = "",
  isDisabled = false,
}: IProps) => {
  const intl = useIntl();
  const [value, setValue] = useState("");
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [queryParameters, setQueryParameters] = useState<IQueryParams>();

  const debouncedValue = useDebounce(value, DEFAULT_DEBOUNCE_TIME);

  useEffect(() => {
    if (milestoneTemplate) {
      const validationCheck = !isDuplicate && !!milestoneTemplate[propertyName];
      setIsValid((prev) => ({ ...prev, [propertyName]: validationCheck }));
    }
  }, [isDuplicate, milestoneTemplate, propertyName, setIsValid]);

  const { data: milestoneTemplates, isError } =
    useSearchMilestoneTemplatesByPropertiesQuery(queryParameters, {
      skip: !queryParameters,
    });

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "milestoneTemplates.searchError" }));
  }, [intl, isError]);

  useEffect(() => {
    if (!milestoneTemplates) return;

    const duplicateExists = doesDuplicateMilestoneValueExists(
      milestoneTemplates,
      propertyName,
      value,
      milestoneTemplate?.id
    );

    setIsDuplicate(duplicateExists);
  }, [milestoneTemplate?.id, milestoneTemplates, propertyName, value]);

  useEffect(() => {
    const propertyValue = milestoneTemplate[propertyName];
    // Use this to set the value when the user selects a value from the dropdown
    if (selectedValue) {
      setValue(selectedValue);
    } else if (typeof propertyValue === "string") {
      setValue(propertyValue);
    }
  }, [milestoneTemplate, propertyName, selectedValue]);

  useEffect(() => {
    if (!value) {
      setIsDuplicate(false);
      return;
    }

    if (debouncedValue !== value) return;

    if (projectMilestoneTemplates && projectMilestoneTemplates.length > 0) {
      const duplicateToComparisonExists = doesDuplicateMilestoneValueExists(
        projectMilestoneTemplates,
        propertyName,
        value,
        milestoneTemplate?.id
      );

      setIsDuplicate(duplicateToComparisonExists);
    }
  }, [
    debouncedValue,
    intl,
    milestoneTemplate?.id,
    propertyName,
    value,
    projectMilestoneTemplates,
  ]);

  const isFieldInvalid = (value: string): boolean => value.startsWith(" ");

  const onChange = (value: string): void => {
    if (isFieldInvalid(value)) return;

    setProjectMilestoneTemplate({
      ...milestoneTemplate,
      [propertyName]: value,
    });
  };

  return (
    <>
      <Typography.Text
        data-test-id={`name-and-abbreviation-editor-${propertyName.toLowerCase()}-label`}
      >
        {intl.formatMessage({
          id: `milestoneTemplates.${propertyName.toLowerCase()}Label`,
        })}
      </Typography.Text>
      <Input
        data-test-id={`name-and-abbreviation-editor-${propertyName.toLowerCase()}-input`}
        value={value}
        disabled={isDisabled}
        onChange={({ target: { value } }): void => onChange(value)}
      />
      {isDuplicate && !selectedValue && (
        <S.StyledTag
          data-test-id={`name-and-abbreviation-editor-dup-${propertyName.toLowerCase()}-error-label`}
        >
          {intl.formatMessage({
            id: `milestoneTemplates.${propertyName}DupError`,
          })}
        </S.StyledTag>
      )}
    </>
  );
};
